/**
 * MemoComposeContext — Global state for the Gmail-style floating memo
 * composer (notes AND tasks — `entityType` in the config picks the family).
 *
 * Provides three high-level intent functions:
 *   openMemoCompose()                                        — blank note
 *   openMemoCompose({ linkedEntities: [...] })               — new note linked to entities
 *   openMemoCompose({ entityType: 'ahoy_task', date, stageEntityId }) — new task
 *   editMemo(entityId)                                       — edit an existing note or task (panel fetches everything)
 *
 * The compose panel owns all data fetching and mutation logic. Callers
 * never need to assemble note data, resolve associations, or pass org context.
 */

/* eslint-disable react-refresh/only-export-components -- standard context pattern: provider, hook, and context co-located */

import type { ReactNode } from 'react';
import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';

// ---------------------------------------------------------------------------
// Configuration types
// ---------------------------------------------------------------------------

/**
 * An entity to pre-link to the note when the compose panel opens.
 * The compose panel resolves the correct `ahoy_note_to_*` association
 * type at submit time using useAssociationMatchers.
 */
export interface MemoComposeLinkedEntity {
  entityId: string;
  entityTypeName: string;
}

/**
 * Configuration for opening the note compose panel in CREATE mode.
 * All fields are optional — an empty config opens a blank note.
 */
export interface MemoComposeConfig {
  /**
   * Which memo type to create: `'ahoy_note'` (default) or `'ahoy_task'`. This
   * is the requested type and is kept SEPARATE from the optional stage — a
   * task stays a task even when it has no stage or the user clears its
   * preselected one (epic #13640: task-ness is no longer inferred from stage
   * presence).
   */
  entityType?: 'ahoy_note' | 'ahoy_task';
  /**
   * Pre-fill the due date. Either a plain ISO date (`"2024-01-15"`, stored on
   * `ahoy_date`) or a full ISO datetime (`"2024-01-15T14:30:00.000Z"`, stored
   * on `ahoy_datetime` — used e.g. when a timed calendar slot is clicked).
   */
  date?: string;
  /** Pre-select a pipeline stage. */
  stageEntityId?: string;
  /** Entities to pre-link to the note (meetings, contacts, deals, etc.). */
  linkedEntities?: MemoComposeLinkedEntity[];
}

// ---------------------------------------------------------------------------
// Internal state
// ---------------------------------------------------------------------------

interface MemoComposeStateCreate {
  isOpen: true;
  mode: 'create';
  config?: MemoComposeConfig;
}

interface MemoComposeStateEdit {
  isOpen: true;
  mode: 'edit';
  memoEntityId: string;
}

interface MemoComposeStateClosed {
  isOpen: false;
}

type MemoComposeState =
  | MemoComposeStateCreate
  | MemoComposeStateEdit
  | MemoComposeStateClosed;

// ---------------------------------------------------------------------------
// Context types
// ---------------------------------------------------------------------------

type MemoComposeActions = {
  /** Open the composer to create a new note with optional pre-filled config. */
  openMemoCompose: (config?: MemoComposeConfig) => void;
  /** Open the composer to edit an existing memo. The panel fetches everything. */
  editMemo: (memoEntityId: string) => void;
  /** Close the composer and reset state. */
  closeMemoCompose: () => void;
};

const INITIAL_STATE: MemoComposeStateClosed = { isOpen: false };

/** No-op fallback when no provider is mounted (safe for tests). */
const NO_OP_ACTIONS: MemoComposeActions = {
  openMemoCompose: () => {},
  editMemo: () => {},
  closeMemoCompose: () => {},
};

// Stable actions context — never triggers re-renders on open/close.
const MemoComposeActionsContext = createContext<MemoComposeActions | null>(
  null
);

// State context — only consumed by the panel renderer component.
const MemoComposeStateContext = createContext<MemoComposeState | null>(null);

// Draft-due contexts. Kept SEPARATE from the state context so the calendar's
// live "ghost" marker can subscribe to the in-progress task's date/time
// without re-rendering every compose consumer on each date change. The value
// is the composer's current resolved due (undefined / YYYY-MM-DD / ISO
// datetime); the setter is wired by the create panel's MemoForm.onDueChange.
const MemoComposeDraftContext = createContext<string | undefined>(undefined);
const MemoComposeDraftSetterContext = createContext<
  (due: string | undefined) => void
>(() => {});

// ---------------------------------------------------------------------------
// Hooks
// ---------------------------------------------------------------------------

/**
 * Access the note compose panel actions (open/close/edit).
 * Returns a no-op implementation when used outside a provider (safe for tests).
 * Consumers of this hook do NOT re-render when the panel opens or closes.
 */
export function useMemoCompose(): MemoComposeActions {
  const ctx = useContext(MemoComposeActionsContext);
  return ctx ?? NO_OP_ACTIONS;
}

/**
 * Read compose state for the panel renderer.
 * Not part of the public API — only MemoComposeRenderer should call this.
 */
export function useMemoComposeState(): MemoComposeState {
  const ctx = useContext(MemoComposeStateContext);
  return ctx ?? INITIAL_STATE;
}

/**
 * Read the composer's current draft due — the date (or date+time) of the task
 * being composed, or `undefined` when none is set / the composer is closed.
 * Consumed by the calendar to render a live "ghost" marker.
 */
export function useMemoComposeDraftDue(): string | undefined {
  return useContext(MemoComposeDraftContext);
}

/**
 * Set the composer's draft due. Wired by the create panel to MemoForm's
 * onDueChange; not part of the public open/close API.
 */
export function useSetMemoComposeDraftDue(): (due: string | undefined) => void {
  return useContext(MemoComposeDraftSetterContext);
}

// ---------------------------------------------------------------------------
// Provider
// ---------------------------------------------------------------------------

export function MemoComposeProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<MemoComposeState>(INITIAL_STATE);
  // The composer's current resolved due, for the calendar's ghost marker.
  // Seeded from the create config's date so the marker appears immediately,
  // then refined live by MemoForm.onDueChange. Cleared on every transition so
  // a stale due from a prior session never lingers.
  const [draftDue, setDraftDue] = useState<string | undefined>(undefined);

  const openMemoCompose = useCallback((config?: MemoComposeConfig) => {
    setState({ isOpen: true, mode: 'create', config });
    setDraftDue(config?.date);
  }, []);

  const editMemo = useCallback((memoEntityId: string) => {
    setState({ isOpen: true, mode: 'edit', memoEntityId });
    // No ghost marker in edit mode — the real task is already on the grid.
    setDraftDue(undefined);
  }, []);

  const closeMemoCompose = useCallback(() => {
    setState(INITIAL_STATE);
    setDraftDue(undefined);
  }, []);

  const actions = useMemo<MemoComposeActions>(
    () => ({ openMemoCompose, editMemo, closeMemoCompose }),
    [openMemoCompose, editMemo, closeMemoCompose]
  );

  // React 19 renders a context object directly as its provider — see
  // docs/claude/REACT_19_PATTERNS.md; `.Provider` is deprecated.
  return (
    <MemoComposeActionsContext value={actions}>
      <MemoComposeStateContext value={state}>
        <MemoComposeDraftSetterContext value={setDraftDue}>
          <MemoComposeDraftContext value={draftDue}>
            {children}
          </MemoComposeDraftContext>
        </MemoComposeDraftSetterContext>
      </MemoComposeStateContext>
    </MemoComposeActionsContext>
  );
}
